import { bigDealAmount, coinRainAmount } from "../Dashboard/constants";
import { byId } from "../Dashboard/animations/registry";

// De rekenkant van het animatielab. Elke animatie hoort bij iets wat er in
// het echt gebeurt, dus in het lab verandert hier ook echt een bedrag: de
// balk groeit, de bedragen tellen op, de ranglijst schuift. Zonder dat kijk
// je naar een plaatje dat over een stilstaand scherm heen beweegt.

const pick = (list) => list[Math.floor(Math.random() * list.length)];

// Een bedrag dat er als een echte deal uitziet: hele vijftigtallen.
const roundDeal = (min, max) =>
  Math.round((min + Math.random() * (max - min)) / 50) * 50;

const bySize = (a, b) => (b.totalPrice || 0) - (a.totalPrice || 0);

// Telt een bedrag op bij een naam in een lijst en zet de lijst weer op
// volgorde. De lijst zelf blijft ongemoeid: het dashboard vergelijkt oud met
// nieuw, dus we mogen niets ter plekke aanpassen.
function addTo(list, name, amount) {
  const found = list.some((row) => row.name === name);
  const next = found
    ? list.map((row) =>
        row.name === name
          ? { ...row, totalPrice: (row.totalPrice || 0) + amount }
          : row,
      )
    : [...list, { name, totalPrice: amount }];

  return next.sort(bySize);
}

// Verwerkt één verkoop in alle cijfers die het dashboard laat zien.
export function applySale(data, { name, service, amount }) {
  const rankList = addTo(data.rankList, name, amount);
  const soldServices = service
    ? addTo(data.soldServices, service, amount)
    : data.soldServices;

  const total = data.amount + amount;
  const target = data.teamTarget.amount || 0;

  // De bovenste rij van de jaarlijst is de lopende maand; die hoort mee te
  // bewegen als er iemand anders bovenaan komt te staan.
  const leader = rankList[0];
  const yearRankList = data.yearRankList.length
    ? [
        {
          ...data.yearRankList[0],
          name: leader.name,
          totalPrice: leader.totalPrice,
        },
        ...data.yearRankList.slice(1),
      ]
    : data.yearRankList;

  return {
    ...data,
    rankList,
    soldServices,
    yearRankList,
    amount: total,
    scoredTowardsTarget: target ? Math.min(target, total) : total,
  };
}

// De negen cijfertoetsen van het lab: drie verkopers, drie bedragen.
// Toets 1..3 is Ernst, 4..6 Bobbie, 7..9 Bert. Zo weet je van tevoren wat
// er gaat gebeuren: een klein bedrag, een deal vanaf coinRainAmount (munten)
// en een deal vanaf bigDealAmount (munten + diamantregen).
export const labSellers = ["Ernst", "Bobbie", "Bert"];
export const labAmounts = [100, 500, 1100];

export const payments = labSellers.flatMap((name, row) =>
  labAmounts.map((amount, column) => ({
    key: String(row * 3 + column + 1),
    name,
    amount,
  })),
);

// Een betaling van de cijfertoetsen. De dienst wisselt, zodat de kolom
// "Verkochte diensten" ook meebeweegt.
export function paymentSale(data, payment) {
  return {
    name: payment.name,
    service: pick(data.soldServices).name,
    amount: payment.amount,
  };
}

// Een willekeurige verkoop, zoals er de hele dag door binnenkomen.
export function randomSale(data, { min = 250, max = 2200 } = {}) {
  return {
    name: pick(data.rankList).name,
    service: pick(data.soldServices).name,
    amount: roundDeal(min, max),
  };
}

// Wat er bij deze verkoper nog bij moet om de plek erboven te pakken.
// Staat er niemand boven, dan is er niets in te halen.
function jumpOver(data, index) {
  const climber = data.rankList[index];
  const above = data.rankList[index - 1];
  if (!climber || !above) return null;

  const gap = (above.totalPrice || 0) - (climber.totalPrice || 0);
  return {
    name: climber.name,
    service: pick(data.soldServices).name,
    amount: Math.max(50, Math.round((gap + roundDeal(300, 900)) / 50) * 50),
    from: index + 1,
    position: index,
  };
}

// Per animatie: wat er met de cijfers gebeurt en wat de animatie te zien
// krijgt. Geeft { data, payload, sale } terug - sale is puur om in het lab
// te tonen wat er zojuist veranderd is.
export function scenarioFor(id, data) {
  const nothing = { data, payload: {}, sale: null };

  switch (id) {
    // Een gewone verkoop.
    case "sale-splash":
    case "sale-bar":
    case "row-flash":
    case "gauge-pulse":
    case "screen-glow": {
      const sale = randomSale(data, { min: 250, max: 1800 });
      return { data: applySale(data, sale), payload: sale, sale };
    }

    // Een deal waar je iets van mag vinden.
    case "coin-rain": {
      const sale = randomSale(data, { min: coinRainAmount, max: bigDealAmount - 1 });
      return { data: applySale(data, sale), payload: sale, sale };
    }

    // Grote deal: diamantregen (en de munten die daarbij ook meevallen).
    case "gem-rain": {
      const sale = randomSale(data, { min: bigDealAmount, max: 4500 });
      return { data: applySale(data, sale), payload: sale, sale };
    }

    // Confetti hoort in het echt bij het bereiken van het teamtarget,
    // in het lab tonen we hem los.
    case "confetti-burst":
      return nothing;

    // Record: iemand uit de middenmoot zet de grootste verkoop ooit neer.
    case "fireworks": {
      const middle = Math.min(3, Math.max(1, data.rankList.length - 2));
      const seller = data.rankList[middle] || data.rankList[0];
      const sale = {
        name: seller.name,
        service: pick(data.soldServices).name,
        amount: roundDeal(3000, 6000),
      };
      // Het oude record ligt er net onder, anders was het geen record.
      const previous = Math.round((sale.amount * 0.85) / 50) * 50;
      return {
        data: applySale(data, sale),
        payload: { ...sale, previous },
        sale,
      };
    }

    // Iemand van onderaf schuift een plek op.
    case "rank-up": {
      const index = Math.min(
        data.rankList.length - 1,
        3 + Math.floor(Math.random() * 2),
      );
      const jump = jumpOver(data, index);
      if (!jump) return nothing;
      return {
        data: applySale(data, jump),
        payload: { name: jump.name, position: jump.position, from: jump.from },
        sale: jump,
      };
    }

    // De top 3 wisselt van volgorde.
    case "podium-bounce": {
      const jump = jumpOver(data, 1 + Math.floor(Math.random() * 2));
      if (!jump) return nothing;
      return { data: applySale(data, jump), payload: {}, sale: jump };
    }

    // Het laatste zetje naar de 100%.
    case "target-hit": {
      const target = data.teamTarget.amount || 0;
      const missing = Math.max(500, target - data.amount);
      const sale = {
        name: pick(data.rankList).name,
        service: pick(data.soldServices).name,
        amount: missing,
      };
      const next = applySale(data, sale);
      return { data: next, payload: { amount: next.amount }, sale };
    }

    // Na het aanzetten van de tv: wat er is verkocht terwijl hij uit stond.
    // "Uit" is in het lab niet na te spelen, dus hier met de vaste
    // voorbeeldcijfers uit de registry; de cijfers op de kaarten blijven
    // staan.
    case "missed-sales":
      return { data, payload: byId("missed-sales").demo, sale: null };

    default:
      return nothing;
  }
}
